import { motion } from "framer-motion";
import PublicLayout from "@/components/PublicLayout";
import { companyLegalName, supportEmail, privacyEmail } from "@/lib/companyBrand";
import { openCookieSettings } from "@/lib/cookieConsent";

const Section = ({ title, children }) => (
  <div className="py-8 border-b border-white/5 last:border-0">
    <h2
      className="text-base font-medium text-white mb-4"
      style={{ fontFamily: "Outfit, sans-serif" }}
    >
      {title}
    </h2>
    <div className="text-sm text-zinc-400 leading-7 space-y-3">{children}</div>
  </div>
);

const Highlight = ({ children }) => (
  <span className="text-zinc-300">{children}</span>
);

const PrivacyPage = () => (
  <PublicLayout>
    <main className="relative z-10 px-4 md:px-8 pt-12 md:pt-20 pb-20 md:pb-32 max-w-2xl mx-auto safe-area-padding">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
      >
        {/* Header */}
        <div className="mb-12">
          <p className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-4">Legal</p>
          <h1
            className="text-3xl md:text-4xl font-light tracking-tight mb-5"
            style={{ fontFamily: "Outfit, sans-serif" }}
          >
            Privacy Policy
          </h1>
          <p className="text-sm text-zinc-500 mb-6">
            Last updated November 2, 2026 &middot; Effective November 2, 2026
          </p>
          <p className="text-sm text-zinc-400 leading-7">
            Hans is built for music instructors who care about their students. We don't
            monetize your data, we don't run ads, and we're not interested in selling
            anything about you to anyone. This policy explains what we collect and why —
            in plain language.
          </p>
          <p className="mt-3 text-sm text-[var(--text-secondary)] leading-7">
            This policy also covers the optional Google Calendar connection in
            Kalver Scheduler, the booking service used with Hans.
          </p>
        </div>

        {/* Sections */}
        <Section title="What we collect">
          <p>
            <Highlight>Your account.</Highlight> When you sign in with Google or Apple,
            we receive your name and email address. Apple may give us a private relay
            address instead of your own, and Google may also share your profile photo.
            If you sign up with an email address and a password, we store only a salted
            bcrypt hash of the password. We never store the password itself and cannot
            read it.
          </p>
          <p>
            <Highlight>Your recordings.</Highlight> Audio is captured from your microphone
            or uploaded for transcription and analysis by the third-party AI services named
            below. Before your first recording, Hans asks you to agree to this. Full
            recordings are deleted when processing succeeds or permanently fails, with
            temporary retention for retries and a seven-day cleanup backstop. With consent,
            short practice clips may be kept separately for up to 30 days to support practice.
          </p>
          <p>
            <Highlight>Your students.</Highlight> You may enter student names, email
            addresses, instruments, skill levels, and lesson notes. This data belongs
            to you and is used only to power your studio.
          </p>
          <p>
            <Highlight>Usage analytics.</Highlight> We use PostHog to see which parts of
            Hans get used. It records the pages and screens you open and the features you
            use, linked to your account ID and plan. It never receives your name, your
            email address or your students' details. On the website it runs only if you
            accept analytics in the cookie banner. In the iPhone app it is on while you use
            the app; the app has no cookie banner, and it does not track you across other
            companies' apps or websites.
          </p>
          <p>
            <Highlight>Crash reports.</Highlight> When the website or the iPhone app hits an
            error, Sentry receives a report so we can fix it. A report holds the error, the
            page or screen, your browser or device model, the operating system and the app
            version, and may include your IP address. A small sample of page load timings is
            sent too. Reports are not linked to your name or email address.
          </p>
          <p>
            <Highlight>Payments.</Highlight> Billing is handled entirely by Stripe (web) or
            Apple In-App Purchase (mobile). We do not store your credit card number.
          </p>
        </Section>

        <Section title="Third-party AI services">
          <p>
            Hans uses two AI services to turn a recording into lesson notes. Each one
            processes the data only to do that work for us.
          </p>
          <p>
            <Highlight>AssemblyAI</Highlight> receives the lesson audio and returns a
            transcript that tells the speakers apart. Student practice recordings are
            transcribed the same way.
          </p>
          <p>
            <Highlight>OpenAI</Highlight> receives the transcript and lesson details such as
            the instrument, earlier summaries and notes. It drafts the lesson summary,
            practice feedback, portfolio text and repertoire guides. When an instructor asks
            for a repertoire guide tailored to a student, OpenAI also receives that
            student&apos;s name, instruments, favorite artist, interests and recent lesson
            summaries. You review a summary before you send it.
          </p>
          <p>
            The written summary stays in Hans. Full recordings are deleted after processing,
            as described under How long we keep it.
          </p>
        </Section>

        <Section title="Google Calendar in Kalver Scheduler">
          <p>
            Connecting Google Calendar is optional and separate from signing in to
            Hans with Google. When you connect, Scheduler receives authorization
            tokens and stores them in encrypted form, together with your connected
            account email, calendar identifier and synchronization settings.
          </p>
          <p>
            When calendar import is enabled, Scheduler reads event identifiers,
            titles, dates, times and availability to show busy periods and prevent
            conflicting bookings. Imported event titles can appear in authorized
            workspace calendar views. This information is stored in Scheduler's
            database to support synchronization and availability checks.
          </p>
          <p>
            When calendar export is enabled, Scheduler sends appointment information
            to Google to create and maintain the corresponding event. This may
            include the appointment title, description, time, time zone and attendee
            name and email. Rescheduling and cancellation update or remove the
            corresponding event. If you select Google Meet, Scheduler also requests
            a meeting link through Google Calendar. Access to these events in Google
            is governed by your Google Calendar sharing settings.
          </p>
          <p>
            Google Calendar data is used for these scheduling features, not for
            advertising, sale to third parties or training AI models. Our hosting
            providers process the stored data to operate Scheduler. Our use and
            transfer of information received from Google APIs follows the{" "}
            <a href="https://developers.google.com/terms/api-services-user-data-policy"
              className="text-[var(--accent-yellow-text)] hover:underline">
              Google API Services User Data Policy
            </a>, including its Limited Use requirements.
          </p>
          <p>
            You can turn import or export off, or disconnect the integration in
            Scheduler. Disconnecting removes the connection credentials and imported
            busy periods for that connection and stops future synchronization.
            Existing events in Google Calendar are not automatically deleted by
            disconnection. Scheduler appointment history and synchronization
            identifiers can remain with the business's records. You can separately
            remove the app's access through your Google Account settings.
          </p>
          <p>
            To request access, correction or deletion of Calendar data stored by
            Kalver, contact{" "}
            <a href={`mailto:${privacyEmail()}`} className="text-[var(--accent-yellow-text)] hover:underline">
              {privacyEmail()}
            </a>.
          </p>
        </Section>

        <Section title="How we protect Google user data">
          <p>
            Kalver Scheduler uses technical safeguards to protect Google account
            information, authorization tokens and Calendar data from unauthorized
            access, disclosure and alteration.
          </p>
          <p>
            <Highlight>Encryption.</Highlight> We use HTTPS/TLS to protect data in
            transit between your browser and Scheduler and between Scheduler and
            Google's APIs. Google access and refresh tokens are encrypted before
            they are stored in our database using AES-256-GCM. The encryption key
            is held in server-side deployment secrets, separately from the stored
            tokens and application source code.
          </p>
          <p>
            <Highlight>Access controls.</Highlight> Viewing imported event details
            and managing Calendar connections in Scheduler require a signed-in
            account with the relevant workspace permissions. Server-side workspace
            and calendar permission checks enforce those restrictions. Authorization
            tokens are used on the server and are not displayed on public booking
            pages. Disconnecting the integration removes its stored credentials
            and imported busy periods, as described above.
          </p>
        </Section>

        <Section title="How we use it">
          <ul className="space-y-2.5">
            {[
              "Transcribe and analyze your lessons to generate summaries and practice items",
              "Send lesson summary emails to students and parents at your request",
              "Manage your account, subscription, and billing",
              "Improve the product through usage analytics and crash reports",
              "Communicate with you about updates or support",
              "Comply with legal obligations",
            ].map((item) => (
              <li key={item} className="flex items-start gap-3">
                <span className="w-1 h-1 rounded-full bg-zinc-600 shrink-0 mt-2.5" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </Section>

        <Section title="How long we keep it">
          <p>
            <Highlight>Full lesson recordings</Highlight> are deleted after processing succeeds
            or permanently fails. Audio awaiting retry is retained temporarily, with a seven-day
            cleanup backstop. Consented practice clips are kept separately for up to 30 days.
          </p>
          <p>
            <Highlight>Lesson notes and summaries</Highlight> are retained indefinitely on
            all plans — these are the primary record of your teaching work and are stored
            securely for as long as your account is active.
          </p>
          <p>
            <Highlight>Your account</Highlight> is retained as long as you use Hans. When
            you delete your account, all associated data is permanently removed within 30 days.
          </p>
        </Section>

        <Section title="Third-party services">
          <p>
            We use a small set of trusted third-party services to operate Hans. Each processes
            data only as needed to provide its function:
          </p>
          <div className="mt-3 space-y-2">
            {[
              ["Google & Apple", "Sign-in authentication; Google also provides the optional Scheduler Calendar and Meet integration"],
              ["Apple", "App Store purchases and subscriptions in the iPhone app"],
              ["AssemblyAI", "Third-party AI: transcription of lesson and practice audio"],
              ["OpenAI (GPT-4o mini)", "Third-party AI: lesson summaries, practice feedback, portfolio text and repertoire guides"],
              ["Amazon Web Services", "Cloud infrastructure (storage, compute, CDN)"],
              ["Fly.io", "Scheduler application hosting"],
              ["Stripe", "Payment processing for web subscriptions"],
              ["Resend", "Transactional email delivery"],
              ["Brevo", "Product-update contact list (your email, never your students')"],
              ["Twilio", "SMS delivery of consent links to parents, where enabled"],
              ["PostHog", "Usage analytics: on the website only with your consent, and in the iPhone app"],
              ["Sentry", "Crash and error reports from the website and the iPhone app"],
            ].map(([name, desc]) => (
              <div key={name} className="flex items-baseline gap-2">
                <span className="text-zinc-300 min-w-0 shrink-0">{name}</span>
                <span className="text-zinc-600 text-xs">—</span>
                <span className="text-zinc-500">{desc}</span>
              </div>
            ))}
          </div>
        </Section>

        <Section title="Your rights">
          <p>You can request any of the following by emailing us:</p>
          <div className="mt-3 space-y-2.5">
            {[
              ["Access", "A copy of the personal data we hold about you"],
              ["Correction", "Fix any inaccurate information"],
              ["Deletion", "Remove your account and all associated data"],
              ["Portability", "An export of your data in a machine-readable format"],
            ].map(([right, desc]) => (
              <div key={right} className="flex items-start gap-2">
                <span className="text-zinc-300 shrink-0 w-24">{right}</span>
                <span>{desc}</span>
              </div>
            ))}
          </div>
          <p className="mt-4">
            We respond within 30 days. Reach us at{" "}
            <a href={`mailto:${supportEmail()}`} className="text-[#f4cf08] hover:underline">
              {supportEmail()}
            </a>
            .
          </p>
        </Section>

        <Section title="Cookies">
          <p>
            Hans uses two kinds of cookie: the ones that sign you in, and analytics — which
            stay off unless you turn them on. We do not use advertising cookies and do not
            participate in any ad network.
          </p>
          <p>
            <Highlight>session_token</Highlight> authenticates your instructor session and
            expires after 7 days. <Highlight>student_session_token</Highlight> does the
            same for the student portal, and there are equivalents for the parent and
            organization portals. All are set as httponly and secure — they cannot be
            accessed by JavaScript. These are strictly necessary to sign you in, so they
            are not optional.
          </p>
          <p>
            For analytics we use <Highlight>PostHog</Highlight> to see which parts of Hans
            get used, so we know what to improve. It records page views and clicks against
            your account id and subscription plan. It does <Highlight>not</Highlight>{" "}
            receive your name or your email address, and we do not record your screen.
          </p>
          <p>
            Analytics cookies are <Highlight>off by default</Highlight> and are only set if
            you accept them in the cookie banner. You can change your mind at any time:
          </p>
          <p data-testid="privacy-ios-analytics">
            The cookie banner covers the website. The iPhone app sets no cookies and shows
            no banner. In the app, PostHog records the screens you open against your account
            ID and plan, as described under Usage analytics.
          </p>
          <p>
            <button
              type="button"
              onClick={openCookieSettings}
              className="text-[#f4cf08] underline underline-offset-4 hover:opacity-80 transition-opacity duration-200"
            >
              Cookie settings
            </button>
          </p>
        </Section>

        {/* Children's privacy / COPPA — drafted plain language; attorney
            review recommended. */}
        <Section title="Children's privacy (COPPA)">
          <p>
            Music instructors use Hans to record lessons with students, including students
            under 13. When a lesson involves a minor, recording is{" "}
            <Highlight>locked until consent is on file</Highlight>: the parent or guardian
            receives a consent request describing exactly what is collected and how it is
            used, and nothing is recorded until they agree.
          </p>
          <p>
            <Highlight>What we collect about a child:</Highlight> the child's name, the
            parent's contact email, and audio recordings of the child's music lessons.
          </p>
          <p>
            <Highlight>How it is used:</Highlight> lesson audio is transcribed and
            summarized by service providers acting on our behalf (the third-party AI
            services AssemblyAI and OpenAI), solely to produce the
            lesson summary the instructor sends to the parent. The audio recording is{" "}
            <Highlight>permanently deleted after processing</Highlight>.
            Children's information is never sold and never used for advertising. Beyond
            the service providers that perform transcription and summarization for us, it
            is shared in exactly one case: when a lesson is part of a program run by an
            organization (for example, a music school your instructor teaches through),
            administrators of that organization can view the lesson summaries for that
            program. The consent form names the organization when this applies.
          </p>
          <p>
            <Highlight>For children under 13</Highlight>, we obtain verifiable parental
            consent using the FTC-recognized "email-plus" method: after a parent agrees,
            we send a follow-up confirmation email with a cancellation link, so a consent
            that wasn't genuinely given by the parent can be revoked immediately.
          </p>
          <p>
            <Highlight>Parental rights.</Highlight> A parent can review what we hold,
            withdraw consent at any time (every summary email contains a standing link),
            and require deletion of their child's information. Withdrawing consent locks
            future recording and permanently deletes stored audio and transcripts. To also
            delete lesson summaries or request a copy of your child's information, contact
            us at{" "}
            <a href={`mailto:${privacyEmail()}`} className="text-[#f4cf08] hover:underline">
              {privacyEmail()}
            </a>
            . We will not condition a child's participation in lessons on disclosing more
            information than is reasonably necessary.
          </p>
          <p>
            If you believe we've collected information from a child under 13 without
            verifiable parental consent, contact us at{" "}
            <a href={`mailto:${privacyEmail()}`} className="text-[#f4cf08] hover:underline">
              {privacyEmail()}
            </a>{" "}
            and we'll remove it promptly. Operator: {companyLegalName()}
          </p>
        </Section>

        <Section title="California residents (CCPA)">
          <p>
            If you're a California resident, you have the right to know what personal
            information we collect and how it's used, to request its deletion, and to
            opt out of any sale of personal information.{" "}
            <strong className="text-zinc-300">We do not sell your personal information.</strong>
          </p>
        </Section>

        <Section title="International users">
          <p>
            Hans is operated from the United States. By using the Service from outside
            the US, you understand that your data may be transferred to and stored in
            the US. We take reasonable steps to ensure it's handled securely regardless
            of where you're located.
          </p>
        </Section>

        <Section title="Changes to this policy">
          <p>
            We'll update the date at the top of this page when we make changes. For material
            changes, we'll notify you by email or in-app notification. Your continued use of
            Hans after changes are posted means you accept the updated policy.
          </p>
        </Section>

        {/* Footer contact */}
        <div className="pt-10 mt-2">
          <p className="text-sm text-zinc-500">
            Questions about how we handle your data?{" "}
            <a href={`mailto:${supportEmail()}`} className="text-[#f4cf08] hover:underline">
              {supportEmail()}
            </a>
          </p>
        </div>
      </motion.div>
    </main>
  </PublicLayout>
);

export default PrivacyPage;
